/**
 * Parse AI Intelligence Note markdown into a clean, sectioned structure
 * suitable for officer-facing UI (headers + bullet points, no raw # / **).
 */

export interface IntelligenceNoteSection {
    heading: string;
    bullets: string[];
}

export interface ParsedIntelligenceNote {
    title: string;
    sections: IntelligenceNoteSection[];
}

/** Strip inline markdown emphasis/code while keeping the readable text. */
export function stripInlineMd(text: string): string {
    return text
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/(^|[^\w])__(.+?)__(?=[^\w]|$)/g, '$1$2')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/(^|[^\w])_(.+?)_(?=[^\w]|$)/g, '$1$2')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .trim();
}

/** Split a prose paragraph into concise bullet-sized sentences. */
export function proseToBullets(paragraph: string): string[] {
    const cleaned = stripInlineMd(paragraph).replace(/\s+/g, ' ').trim();
    if (!cleaned) return [];

    // Already short — keep as one bullet.
    if (cleaned.length < 140 && !/[.!?]\s+[A-Z0-9]/.test(cleaned)) {
        return [cleaned];
    }

    const parts = cleaned
        .split(/(?<=[.!?])\s+(?=[A-Z0-9"'])/)
        .map((s) => s.trim())
        .filter(Boolean);

    if (parts.length <= 1) return [cleaned];

    // Merge tiny fragments into the previous bullet so we don't get choppy UI.
    const bullets: string[] = [];
    for (const part of parts) {
        if (bullets.length > 0 && part.length < 40) {
            bullets[bullets.length - 1] = `${bullets[bullets.length - 1]} ${part}`.trim();
        } else {
            bullets.push(part);
        }
    }
    return bullets;
}

function normalizeHeading(raw: string): string {
    return stripInlineMd(raw.replace(/^#+\s*/, ''));
}

/**
 * Parse markdown Intelligence Notes into title + sections with bullets.
 * Handles both list-style (`- item`) and prose paragraphs (sentence-split).
 */
export function parseIntelligenceNote(markdown: string): ParsedIntelligenceNote {
    const text = (markdown || '').replace(/\r\n/g, '\n').trim();
    if (!text) {
        return { title: 'Intelligence Note', sections: [] };
    }

    const lines = text.split('\n');
    let title = 'Intelligence Note';
    const sections: IntelligenceNoteSection[] = [];
    let current: IntelligenceNoteSection | null = null;
    let proseBuffer: string[] = [];

    const flushProse = () => {
        if (!current || proseBuffer.length === 0) {
            proseBuffer = [];
            return;
        }
        const block = proseBuffer.join(' ').trim();
        proseBuffer = [];
        if (!block) return;
        current.bullets.push(...proseToBullets(block));
    };

    const ensureSection = (heading: string) => {
        flushProse();
        current = { heading, bullets: [] };
        sections.push(current);
    };

    for (const rawLine of lines) {
        const line = rawLine.trimEnd();
        const trimmed = line.trim();
        if (!trimmed) {
            flushProse();
            continue;
        }

        // ATX headings
        const h1 = trimmed.match(/^#\s+(.+)$/);
        if (h1) {
            flushProse();
            title = normalizeHeading(h1[1]);
            continue;
        }
        const h2 = trimmed.match(/^#{2,6}\s+(.+)$/);
        if (h2) {
            ensureSection(normalizeHeading(h2[1]));
            continue;
        }

        // Bold-only line used as a pseudo-heading: **Executive Summary**
        const boldHeading = trimmed.match(/^\*\*(.+?)\*\*:?\s*$/);
        if (boldHeading && !trimmed.includes('  ') && boldHeading[1].length < 80) {
            ensureSection(stripInlineMd(boldHeading[1]));
            continue;
        }

        // List items (indent is already stripped by trim — nested bullets
        // still match the same [-*•] / numbered patterns).
        const listItem = trimmed.match(/^[-*•]\s+(.+)$/) || trimmed.match(/^\d+[.)]\s+(.+)$/);
        if (listItem) {
            flushProse();
            if (!current) ensureSection('Findings');
            current!.bullets.push(stripInlineMd(listItem[1]));
            continue;
        }

        if (!current) {
            // Lead paragraph before any ## — treat as Executive Summary if title already set
            ensureSection('Overview');
        }
        proseBuffer.push(trimmed);
    }

    flushProse();

    // Drop empty sections; rename generic Overview if it's the only lead
    const cleanedSections = sections
        .map((s) => ({
            heading: s.heading,
            bullets: s.bullets.map((b) => b.trim()).filter(Boolean),
        }))
        .filter((s) => s.bullets.length > 0);

    return {
        title: title || 'Intelligence Note',
        sections: cleanedSections,
    };
}
